/**
 * TARP evaluation — where a machine sits on each Trigger Action Response
 * Plan that covers it, since when, and whose move it is.
 *
 * A TARP (`seed/tarps.ts`) is a ladder of levels, each with a
 * machine-readable rule per data basis. For a machine this engine:
 *
 * 1. picks the **basis** — `live` when the machine has a live replay and the
 *    TARP has live rules, else `daily` when the machine's tracked metric is
 *    the TARP's metric, else `events`;
 * 2. builds the series on that basis — live samples, daily means, or one
 *    point per calendar day for event rules;
 * 3. evaluates every point's level (highest level whose rule holds on the
 *    window ending at that point);
 * 4. reports the **latest** point's level and **since** — the first point of
 *    the unbroken run at that level.
 *
 * Event windows count back from each day inclusive: a 3-day window on
 * `2026-09-08` covers 06, 07 and 08 Sep. The event series runs to
 * `OEM_TODAY` (09 Sep) because the dataset carries that morning's events.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions over
 * fetched data. No React, no clock, no randomness.
 */

import { BY_ID } from '../../data/core.ts';
import type { Tarp, TarpBasis, TarpLevel, TarpRule, TarpStatus } from '../../schema/operations.ts';
import type { DailyMetricRow, EventDailyRow, LiveReplayRegistry } from '../../schema/telemetry.ts';
import { OEM_TODAY } from '../oem-clock.ts';
import { TARPS } from '../seed/tarps.ts';
import { addDays, daysBetween } from './hash.ts';
import { loadReliabilityInputs } from './inputs.ts';
import { liveSeries } from './spc.ts';

/** What TARP evaluation reads. */
export interface TarpCtx {
  readonly dailyMetric: readonly DailyMetricRow[];
  readonly eventsDaily: readonly EventDailyRow[];
  readonly live?: LiveReplayRegistry;
  /** Last day of the event series. Default `OEM_TODAY`. */
  readonly asOf?: string;
}

/** The TARPs that cover a machine — by class, and battery-only where marked. */
export function tarpsFor(machineId: string): Tarp[] {
  const m = BY_ID[machineId];
  if (!m) return [];
  return TARPS.filter(
    (t) => (t.class === m.cls || t.alsoClasses?.includes(m.cls)) && (t.appliesTo !== 'battery' || m.battery),
  );
}

interface Point {
  x: string;
  value: number | null;
}

/** Does `rule` hold on the window ending at index `i`? */
function holds(rule: TarpRule, points: readonly Point[], i: number, eventDays: ReadonlyMap<string, number>[] | null): boolean {
  switch (rule.kind) {
    case 'above': {
      const w = points.slice(Math.max(0, i - rule.window + 1), i + 1);
      return w.filter((p) => p.value !== null && p.value > rule.threshold).length >= rule.count;
    }
    case 'drop-pct': {
      const base = points.slice(0, rule.baselineN).map((p) => p.value).filter((v): v is number => v !== null);
      const v = points[i].value;
      if (!base.length || v === null || i < rule.baselineN) return false;
      const mean = base.reduce((a, b) => a + b, 0) / base.length;
      return v <= mean * (1 - rule.pct / 100);
    }
    case 'event-count': {
      if (!eventDays) return false;
      let n = 0;
      for (let k = Math.max(0, i - rule.windowDays + 1); k <= i; k += 1) {
        for (const code of rule.codes) n += eventDays[k].get(code) ?? 0;
      }
      return n >= rule.count;
    }
  }
}

/** The highest level whose rule on `basis` holds at index `i`; `Normal` when none does. */
function levelAt(
  tarp: Tarp,
  basis: TarpBasis,
  points: readonly Point[],
  i: number,
  eventDays: ReadonlyMap<string, number>[] | null,
): TarpLevel {
  for (let l = tarp.levels.length - 1; l >= 1; l -= 1) {
    const rule = tarp.levels[l].rules?.[basis];
    if (rule && holds(rule, points, i, eventDays)) return tarp.levels[l];
  }
  return tarp.levels[0];
}

/** Evaluate one TARP for one machine. */
export function tarpStatus(machineId: string, tarp: Tarp, ctx: TarpCtx): TarpStatus {
  const hasLive = tarp.levels.some((l) => l.rules?.live);
  const hasDaily = tarp.levels.some((l) => l.rules?.daily);
  const live = hasLive && ctx.live ? liveSeries(machineId, ctx.live) : null;
  const daily = ctx.dailyMetric.filter((d) => d.machine_id === machineId && d.metric === tarp.metric);

  let basis: TarpBasis;
  let points: Point[];
  let eventDays: Map<string, number>[] | null = null;

  if (live) {
    basis = 'live';
    points = live.points.map((p) => ({ x: p.x, value: p.value }));
  } else if (hasDaily && daily.length) {
    basis = 'daily';
    points = daily.map((d) => ({ x: d.date, value: d.value }));
  } else {
    basis = 'events';
    const mine = ctx.eventsDaily.filter((e) => e.machine_id === machineId);
    const asOf = ctx.asOf ?? OEM_TODAY;
    const first = ctx.eventsDaily.reduce((min, e) => (e.day < min ? e.day : min), asOf);
    const span = daysBetween(first, asOf);
    points = [];
    eventDays = [];
    for (let k = 0; k <= span; k += 1) {
      const day = addDays(first, k);
      const counts = new Map<string, number>();
      for (const e of mine) if (e.day === day) counts.set(e.code, (counts.get(e.code) ?? 0) + e.n);
      points.push({ x: day, value: null });
      eventDays.push(counts);
    }
  }

  const levels = points.map((_, i) => levelAt(tarp, basis, points, i, eventDays));
  const last = levels.length - 1;
  const current = last >= 0 ? levels[last] : tarp.levels[0];
  let start = last;
  while (start > 0 && levels[start - 1].level === current.level) start -= 1;
  const since = last < 0 || (current.level === 'Normal' && start === 0) ? null : points[start].x;

  return {
    tarpId: tarp.id,
    machine: machineId,
    level: current.level,
    colour: current.colour,
    since,
    latest: last >= 0 ? points[last].value : null,
    basis,
    trigger: current.trigger,
    actions: current.actions,
    owner: current.owner,
  };
}

/** Every TARP covering a machine, evaluated. */
export function tarpStatuses(machineId: string, ctx: TarpCtx): TarpStatus[] {
  return tarpsFor(machineId).map((t) => tarpStatus(machineId, t, ctx));
}

const LEVEL_RANK: Readonly<Record<TarpStatus['level'], number>> = {
  Normal: 0,
  'Level 1': 1,
  'Level 2': 2,
  'Level 3': 3,
};

/** Every machine × covering TARP above Normal, worst first. */
export function fleetTarpStatuses(ctx: TarpCtx, machines: readonly string[] = Object.keys(BY_ID)): TarpStatus[] {
  return machines
    .flatMap((m) => tarpStatuses(m, ctx))
    .filter((s) => s.level !== 'Normal')
    .sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level] || a.machine.localeCompare(b.machine));
}

/** Fetch and evaluate one machine. */
export async function loadTarpStatuses(machineId: string): Promise<TarpStatus[]> {
  return tarpStatuses(machineId, await loadReliabilityInputs());
}
